import React, {
  createContext,
  SetStateAction,
  Dispatch,
  useMemo,
  useState,
  useEffect,
} from 'react';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import noop from 'lodash/noop';
import sumBy from 'lodash/sumBy';
import {
  PatientDTO,
} from 'dtos';
import Button from 'pages/Dashboard/components/Button';
import Dialog from 'pages/Dashboard/components/Dialog';
import {
  roundNumber,
} from '@populateui/utils';
import {
  useCreateAllocation,
  useGetAutoAllocationPreview,
  useGetBillingSummary,
} from 'views/EMR/hooks/billing';
import {
  allocationColumns,
  allocationPreviewColumns,
  EncounterFinancialDetails,
} from 'views/EMR/Billing/columns';
import SummaryItem from 'views/EMR/Billing/SummaryItem';
import UnpaidEncounters from 'views/EMR/Billing/UnpaidEncounters';
import {
  useMedia,
  useNotify,
} from 'core/hooks';
import {
  paymentsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import ToolTip from 'core/components/Tooltip';
import Name from 'pages/Dashboard/components/Name';
import {
  DEFAULT_EMPTY_VALUE,
} from 'core/constants';

export type DialogParams = {
  patient: Partial<PatientDTO>;
};

type Props = DialogParams & {
  onClose: () => void;
};

type AllocationMode = 'auto' | 'manual';

type AllocationContextProps = {
  setAllocated: Dispatch<SetStateAction<Partial<EncounterFinancialDetails>[]>>;
  allocated: Partial<EncounterFinancialDetails>[];
  isAuto: boolean;
  allocatedEncounters: Set<number>;
  toggle: (encounter: EncounterFinancialDetails) => void;
  remainder: number;
};

export const AllocationContext = createContext<AllocationContextProps>({
  isAuto: false,
  allocated: [],
  setAllocated: noop,
  allocatedEncounters: new Set(),
  toggle: noop,
  remainder: 0,
});

export default function EncountersAllocation({
  patient,
  onClose,
}: Props) {
  const [allocationMode, setAllocationMode] = useState<AllocationMode>('manual');
  const [remainder, setRemainder] = useState<number>(0);
  const [allocated, setAllocated] = useState<Partial<EncounterFinancialDetails>[]>([]);
  const notify = useNotify();
  const isAuto = allocationMode === 'auto';
  const { patientId } = patient ?? {};
  const { data: summary, isFetched, refetch } = useGetBillingSummary({
    patientId,
    isDeposit: true,
  });
  const {
    data: allocationPreview,
    refetch: refetchAllocationPreview,
    isLoading: isAllocationPreviewLoading,
  } = useGetAutoAllocationPreview(patientId!, isAuto);
  const { mutateAsync: createAllocation } = useCreateAllocation(patientId!, isAuto);

  useEffect(() => {
    if (isAuto && !isAllocationPreviewLoading) {
      setRemainder(summary?.credit ?? 0);
      setAllocated([]);
    }
  }, [isAuto, isAllocationPreviewLoading]);

  const allocatedAmount = useMemo(() => (
    sumBy(isAuto && !isNil(allocationPreview?.encounterFinancials)
      ? allocationPreview?.encounterFinancials
      : allocated, 'allocated')
  ), [isAuto, allocationPreview?.encounterFinancials, allocated]);

  const allocatedEncounters = useMemo(() => new Set<number>(
    allocated.map(({ encounterId }) => encounterId ?? 0),
  ), [allocated]);

  useEffect(() => {
    if (isFetched) {
      setRemainder(summary?.credit ?? 0);
    }
  }, [isFetched, patientId, summary]);

  useEffect(() => {
    const unsubscribe = paymentsEventChannel.on('onPatientBalanceChange', async () => {
      await refetch();

      if (isAuto) { await refetchAllocationPreview(); }
    });

    return () => { unsubscribe(); };
  }, [patientId, isAuto]);

  const toggle = (encounter: EncounterFinancialDetails) => {
    const encounterIndex = allocated.findIndex((item) =>
      item.encounterId === encounter.encounterId);
    if (allocatedEncounters.has(encounter.encounterId ?? 0)) {
      const { allocated: allocationAmount = 0 } = allocated[encounterIndex];
      setAllocated([
        ...allocated.slice(0, encounterIndex),
        ...allocated.slice(encounterIndex + 1),
      ]);
      setRemainder(remainder + allocationAmount);
    } else if (remainder > 0) {
      const allocationAmount = Math.min(remainder, encounter.financials?.totalDue ?? 0);
      setAllocated([...allocated, {
        ...encounter,
        allocated: allocationAmount,
      }]);
      setRemainder(roundNumber(remainder - allocationAmount));
    }
  };

  const onSubmit = async () => {
    await createAllocation(isAuto ? {} : {
      encounterWiseAllocations: allocated.map(({ encounterId, allocated: amount }) => ({
        encounterId,
        amount,
      })),
    });
    paymentsEventChannel.emit('onPatientBalanceChange');
    notify({
      message: 'Allocation Created Successfully',
      variant: 'success',
    });
    onClose();
  };

  const contextValue = useMemo(() => ({
    isAuto,
    setAllocated,
    allocatedEncounters,
    allocated,
    remainder,
    toggle,
  }), [isAuto, setAllocated, allocated, allocatedEncounters, remainder, toggle]);

  const { getMediaPathById } = useMedia();
  const src = getMediaPathById(patient?.patientId ?? 0) ?? '';

  return (
    <Dialog
      open
      onClose={onClose}
      showActions={false}
      className="overflow-y-hidden !w-176"
      maxWidth="lg"
      disableEscapeKeyDown
      disableBackdropClick
      title={(
        <div className="flex flex-col gap-px text-lg font-semibold">
          <span>Allocate Credit to Encounters</span>
          <small className="text-gray-500">
            <Name
              image={{ src }}
              {...patient?.formattedNames}
              placeholder={DEFAULT_EMPTY_VALUE}
            />
          </small>
        </div>
      )}
    >
      <div className="flex gap-4 flex-col">
        <div className="flex gap-4">
          <SummaryItem
            value={roundNumber((summary?.totalBalance ?? 0) - allocatedAmount)}
            title="Total balance overdue"
          />
          <SummaryItem
            value={isAuto ? roundNumber((summary?.credit ?? 0) - allocatedAmount) : remainder}
            title="Patient's credit"
          />
        </div>
        <div className="flex flex-1 w-full overflow-auto populate-breakdown-table">
          <AllocationContext.Provider value={contextValue}>
            <UnpaidEncounters
              patientId={patientId!}
              suggestedData={allocationPreview?.encounterFinancials ?? []}
              columns={allocationMode === 'auto' ? allocationPreviewColumns : allocationColumns}
            />
          </AllocationContext.Provider>
        </div>
      </div>
      <div className="w-full flex justify-between items-center">
        <Button
          variant="outlined"
          onClick={onClose}
        >
          Close
        </Button>
        {!isNil(allocationMode) && (
          <div className="flex gap-4 items-center">
            {isAuto
              ? (
                <Button
                  variant="outlined"
                  onClick={() => { setAllocationMode('manual'); }}
                >
                  Back
                </Button>
              )
              : (
                <ToolTip
                  title="Nothing to allocate."
                  hide={summary?.credit !== 0 && summary?.totalBalance !== 0}
                >
                  <div className={clsx({
                    'cursor-not-allowed': summary?.credit === 0 || summary?.totalBalance === 0,
                  })}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={summary?.credit === 0 || summary?.totalBalance === 0}
                      onClick={() => {
                        setAllocationMode('auto');
                      }}
                    >
                      Auto Allocate
                    </Button>
                  </div>
                </ToolTip>
              )}
            <ToolTip
              title="You need to allocate at least one encounter"
              hide={allocated.length > 0 || allocationMode === 'auto'}
            >
              <div className={clsx({
                'cursor-not-allowed': allocated.length === 0 && allocationMode !== 'auto',
              })}
              >
                <Button
                  variant="contained"
                  color="primary"
                  disabled={allocated.length === 0 && allocationMode !== 'auto'}
                  onClick={onSubmit}
                >
                  Confirm
                </Button>
              </div>
            </ToolTip>
          </div>
        )}
      </div>
    </Dialog>
  );
}
